/**
 * Bot Triagem API clients (Story E9.2.3 / FR-E9-11).
 *
 * Dois axios isolados:
 * - ``api`` (internal JWT) → admin endpoints sob /atendimentos/bot/*.
 * - ``portalApi`` (portal token) → endpoints sob /portal/atendimentos/{id}/triage/*.
 *
 * Backend refs:
 * - V1/backend/app/api/v1/endpoints/atendimento_bot_triage.py (admin)
 * - V1/backend/app/api/v1/endpoints/portal_atendimentos.py (portal)
 */

import api from "@/services/api";
import portalApi from "@/services/portalApi";
import type {
  BotTriageAnswerRequest,
  BotTriageConfig,
  BotTriageConfigUpdateRequest,
  BotTriageMenuTree,
  BotTriageState,
} from "@/types/botTriage";

const ADMIN_PREFIX = "/atendimentos/bot";
const PORTAL_PREFIX = "/portal/atendimentos";

// ---------------------------------------------------------------------------
// Admin (internal JWT)
// ---------------------------------------------------------------------------

export const botTriageAdminApi = {
  getConfig: async (): Promise<BotTriageConfig> => {
    const r = await api.get<BotTriageConfig>(`${ADMIN_PREFIX}/config`);
    return r.data;
  },

  patchConfig: async (body: BotTriageConfigUpdateRequest): Promise<BotTriageConfig> => {
    const r = await api.patch<BotTriageConfig>(`${ADMIN_PREFIX}/config`, body);
    return r.data;
  },

  getMenuTree: async (): Promise<BotTriageMenuTree> => {
    const r = await api.get<BotTriageMenuTree>(`${ADMIN_PREFIX}/menu-tree`);
    return r.data;
  },
};

// ---------------------------------------------------------------------------
// Portal (partner token)
// ---------------------------------------------------------------------------

export const botTriagePortalApi = {
  /**
   * GET state — sem triagem do bot o servidor responde 200 com corpo `null`
   * (devolvido como está). O 404 só sobra para sessão inexistente/alheia e
   * para o servidor antigo, que ainda o usava para "sem triagem": vira null.
   */
  getState: async (sessionId: string): Promise<BotTriageState | null> => {
    try {
      const r = await portalApi.get<BotTriageState>(`${PORTAL_PREFIX}/${sessionId}/triage/state`);
      return r.data;
    } catch (e) {
      // 404 = servidor antigo sem triagem engatada — UI esconde painel.
      const status = (e as { response?: { status?: number } })?.response?.status;
      if (status === 404) return null;
      throw e;
    }
  },

  answer: async (sessionId: string, body: BotTriageAnswerRequest): Promise<BotTriageState> => {
    const r = await portalApi.post<BotTriageState>(
      `${PORTAL_PREFIX}/${sessionId}/triage/answer`,
      body
    );
    return r.data;
  },

  skip: async (sessionId: string): Promise<BotTriageState> => {
    const r = await portalApi.post<BotTriageState>(`${PORTAL_PREFIX}/${sessionId}/triage/skip`, {});
    return r.data;
  },
};
